import { createFileRoute, notFound } from '@tanstack/react-router'
import { DocumentView } from '~/components/documents/render/registry'
import { sampleDocument } from '~/components/documents/render/sample-document'
import { DOCUMENT_DESIGN_KEYS, type DocumentDesignKey } from '~convex/lib/documentDesigns'

/**
 * `/design-preview/$designKey` — one design, rendered from the sample
 * document, on a bare page with no chrome.
 *
 * This exists to be SCREENSHOT. The design-picker cards need a real preview of
 * each of the five, and rendering five live `DocumentView`s inside the
 * editor would mean five trees of Tiptap editors just to draw a card — so the
 * cards use static images, and this route is where those images come from.
 *
 * To regenerate them (after changing a design, the seed defaults, or the
 * sample), run the dev server and capture each key at 1000×1400 with a
 * device-scale factor of 2:
 *
 *   /design-preview/classica → public/design-previews/classica.png
 *
 * `bun run previews` drives that with Playwright, which is already a
 * dependency for the e2e suite.
 *
 * It stays in the shipped bundle rather than being deleted after each capture:
 * the alternative is a route that has to be reinvented every time a design
 * changes, which is how previews go stale. It reads no data, takes no params
 * beyond the key, and renders sample copy only. `design-preview` is in
 * `RESERVED_WORKSPACE_SLUGS` for the same reason `design-system` is — a
 * workspace that claimed the slug would shadow it.
 */
export const Route = createFileRoute('/design-preview/$designKey')({
  component: RouteComponent,
  ssr: false,
})

const isDesignKey = (value: string): value is DocumentDesignKey =>
  (DOCUMENT_DESIGN_KEYS as string[]).includes(value)

function RouteComponent() {
  const { designKey } = Route.useParams()
  if (!isDesignKey(designKey)) {
    throw notFound()
  }
  return (
    // `bg-card` and no page padding: the capture is cropped to the document
    // itself, so any frame here would end up baked into every card.
    <div className="min-h-dvh bg-card" data-design-preview={designKey}>
      <DocumentView document={sampleDocument(designKey)} />
    </div>
  )
}
